// Normalizes backend notification links into the Web route graph.
// This mirrors Flutter's NotificationRouteResolver and preserves the
// existing backend event/link architecture.
export function normalizeNotificationLink(rawLink, notification = {}) {
  const trimmed = (rawLink || '').toString().trim();
  const data = notification?.data || {};
  const eventType = (notification?.event_type || notification?.eventType || '').toString();
  const role = (notification?.role || data.role || '').toString();
  const fallbackRequestId = data.request_id || data.requestId || data.rfq_id || data.rfqId;
  const dealerRole = role.startsWith('dealer') || role === 'spare_dealer';
  const isDealerOpportunity = dealerRole || [
    'request.parts_created',
    'request.garage_rfq_created',
    'request.dealer_rfq_created',
    'request.customer_confirmed',
    'request.dealer_rfq_resolved',
  ].includes(eventType);
  if (!trimmed) {
    return isDealerOpportunity && fallbackRequestId
      ? `/dealer/requests/${encodeURIComponent(String(fallbackRequestId).trim())}`
      : null;
  }

  let url;
  try {
    url = new URL(trimmed, window.location.origin);
  } catch {
    return trimmed.startsWith('/') ? trimmed : null;
  }

  if (url.origin !== window.location.origin) {
    return trimmed;
  }

  const path = url.pathname;
  const params = url.searchParams;
  const requestId = params.get('req')
    || params.get('requestId')
    || params.get('request_id')
    || params.get('id')
    || fallbackRequestId;
  const threadId = params.get('thread')
    || params.get('threadId')
    || params.get('thread_id')
    || params.get('id');

  const encode = (value) => encodeURIComponent((value || '').trim());
  const isDealer = dealerRole;
  const isGarage = role.startsWith('garage');

  if (path === '/admin/support') {
    const ticketId = params.get('ticket') || data.ticket_id || data.ticketId;
    return ticketId ? `/admin/support/${encode(ticketId)}` : '/admin/support';
  }

  if (path === '/garage/incoming') {
    return requestId ? `/garage/incoming/${encode(requestId)}` : '/garage/incoming';
  }
  if (path.startsWith('/garage/incoming/')) {
    const id = decodeURIComponent(path.slice('/garage/incoming/'.length)).trim();
    return id ? `/garage/incoming/${encode(id)}` : '/garage/incoming';
  }

  if (path === '/dealer/incoming') {
    return requestId ? `/dealer/requests/${encode(requestId)}` : '/dealer/requests';
  }
  if (path.startsWith('/dealer/incoming/')) {
    const id = decodeURIComponent(path.slice('/dealer/incoming/'.length)).trim();
    return id ? `/dealer/requests/${encode(id)}` : '/dealer/requests';
  }

  if (path === '/dealer/requests') {
    return requestId ? `/dealer/requests/${encode(requestId)}` : '/dealer/requests';
  }
  if (path.startsWith('/dealer/requests/')) {
    const id = decodeURIComponent(path.slice('/dealer/requests/'.length)).trim();
    return id ? `/dealer/requests/${encode(id)}` : '/dealer/requests';
  }

  if (path === '/dealer/rfqs' || path === '/dealer/rfq') {
    return requestId ? `/dealer/rfqs/${encode(requestId)}` : '/dealer/rfqs';
  }
  if (path.startsWith('/dealer/rfq/')) {
    const id = decodeURIComponent(path.slice('/dealer/rfq/'.length)).trim();
    return id ? `/dealer/rfqs/${encode(id)}` : '/dealer/rfqs';
  }

  if (path === '/requests') {
    if (isDealer) return requestId ? `/dealer/requests/${encode(requestId)}` : '/dealer/requests';
    return requestId ? `/my-requests/${encode(requestId)}` : '/my-requests';
  }
  if (path.startsWith('/requests/')) {
    const id = decodeURIComponent(path.slice('/requests/'.length)).trim();
    if (isDealer) return id ? `/dealer/requests/${encode(id)}` : '/dealer/requests';
    return id ? `/my-requests/${encode(id)}` : '/my-requests';
  }

  if (path === '/my-requests') {
    if (isDealer) return requestId ? `/dealer/requests/${encode(requestId)}` : '/dealer/requests';
    return requestId ? `/my-requests/${encode(requestId)}` : '/my-requests';
  }
  if (path.startsWith('/my-requests/')) {
    const id = decodeURIComponent(path.slice('/my-requests/'.length)).trim();
    if (isDealer) return id ? `/dealer/requests/${encode(id)}` : '/dealer/requests';
    return id ? `/my-requests/${encode(id)}` : '/my-requests';
  }

  if (path === '/messages') {
    const messagesPath = isDealer ? '/dealer/messages' : isGarage ? '/garage/messages' : '/messages';
    return threadId ? `${messagesPath}?thread=${encode(threadId)}` : messagesPath;
  }
  if (path.startsWith('/messages/')) {
    const id = decodeURIComponent(path.slice('/messages/'.length)).trim();
    const messagesPath = isDealer ? '/dealer/messages' : isGarage ? '/garage/messages' : '/messages';
    return id ? `${messagesPath}?thread=${encode(id)}` : messagesPath;
  }

  if (path === '/provider/profile' && isDealer) return '/dealer/profile';
  if (path === '/provider/profile' && isGarage) return '/garage/profile';

  return `${path}${url.search}${url.hash}`;
}

export default normalizeNotificationLink;
